import { autobind } from 'core-decorators';
import React from 'react';
import _ from 'underscore';
import Article from 'app/blocks/article/article';
import { RowMd7, WhiteBlock } from 'app/blocks/blocks';
import { Button, PrimaryButton } from 'app/blocks/buttons/buttons';
import { ID, withCodes2 } from 'app/blocks/common/codes';
import { processing, tryCatch } from 'app/blocks/common/decorators';
import * as Utils from 'app/blocks/common/utils';
import Layout from 'app/blocks/Layout/Layout';
import * as InviteColleagueMiddleware from 'app/blocks/middleware/invite-colleague';
import * as middleware from 'app/blocks/middleware/middleware';
import Title from 'app/blocks/page/__title/page__title';
import OrdersRow from 'app/blocks/Row/Row';
import routes from 'app/pages/routes';
import './invite-colleague.scss';
import validation from './invite-colleague.validation';

@autobind
class InviteColleague extends React.Component {
    state = {
        isLoading: true,
        isInviting: false,

        message: this.props.l('INVITE_COLLEAGUE.MESSAGE'),
        invitations: {
            invitations: [],
        },
    };

    @tryCatch
    @processing('isLoading')
    async componentDidMount() {
        const [data, invitations] = await Promise.all([
            middleware.product.getArticleAndJournal(this.props.articleId),
            InviteColleagueMiddleware.getInvitations(this.props.articleId),
        ]);

        this.setState({
            article: data.article,
            journal: data.journal,
            invitations,
        });
    }

    getInvitation() {
        const { firstName, lastName, email, message } = this.state;

        return {
            firstName,
            lastName,
            email,
            message: message || this.props.l('INVITE_COLLEAGUE.MESSAGE'),
        };
    }

    updateFirstName(firstName) {
        this.setState({ firstName });
    }

    updateLastName(lastName) {
        this.setState({ lastName });
    }

    updateEmail(email) {
        this.setState({ email });
    }

    updateMessage(message) {
        this.setState({ message });
    }

    clear() {
        this.setState({
            firstName: null,
            lastName: null,
            email: null,
            message: '',
            validationResults: undefined,
        });
    }

    @tryCatch.popUpOnly
    @processing('isLoading')
    async invite() {
        const { articleId, l } = this.props;
        const invitation = this.getInvitation();

        try {
            await this.validate(invitation);
        } catch {
            return;
        }

        const invitations = await InviteColleagueMiddleware.invite(articleId, invitation);

        this.clear();
        this.setState({ invitations, message: l('INVITE_COLLEAGUE.MESSAGE') });
    }

    async validate(invitation) {
        try {
            await validation(invitation);

            this.setState({ validationResults: undefined });
        } catch (validationResults) {
            this.setState({ validationResults }, () => {
                Utils.scrollToErrorField();
            });

            throw validationResults;
        }
    }

    isInviteButtonEnabled() {
        const { firstName, lastName, email } = this.state;

        return !!firstName && !!lastName && !!email;
    }

    render() {
        const { l } = this.props;
        const { article, journal, isLoading, invitations, firstName, lastName, email, message, isInviting } =
            this.state;
        const validationResults = (this.state.validationResults && this.state.validationResults.result) || {};
        const isInvitationRemains = invitations.invitationsLeftNumber > 0;
        const formSeleniumId = 'invite-colleague';

        if (isLoading) {
            return <Layout isLoading />;
        }

        return (
            <div className="container">
                <Title>{l('INVITE_COLLEAGUE.TITLE')}</Title>

                <Layout>
                    <RowMd7>
                        <WhiteBlock className="page p_mini m-btm_md">
                            <Article article={article} journal={journal} />
                        </WhiteBlock>

                        {isInvitationRemains && (
                            <WhiteBlock className="page p_mini m-btm_md" data-seleniumid="form-invite-colleague">
                                <Layout isLoading={isInviting}>
                                    <h5>{l('INVITE_COLLEAGUE.TITLE_REMAIN')}</h5>
                                    <h6>
                                        {l('INVITE_COLLEAGUE.ABILITY_INVITE', {
                                            invitationsTotalNumber: invitations.invitationsTotalNumber,
                                        })}
                                    </h6>
                                    <div className="border_top" />
                                    <h6 className="m-btm_md">
                                        {l('INVITE_COLLEAGUE.INVITES_COUNTER', {
                                            invitationsLeftNumber: invitations.invitationsLeftNumber,
                                        })}
                                    </h6>

                                    <OrdersRow
                                        error={validationResults.firstName}
                                        formSeleniumid={formSeleniumId}
                                        label={l('INVITE_COLLEAGUE.FIRST_NAME')}
                                        name="firstName"
                                        onChange={this.updateFirstName}
                                        value={firstName}
                                    />
                                    <OrdersRow
                                        error={validationResults.lastName}
                                        formSeleniumid={formSeleniumId}
                                        label={l('INVITE_COLLEAGUE.LAST_NAME')}
                                        name="lastName"
                                        onChange={this.updateLastName}
                                        value={lastName}
                                    />
                                    <OrdersRow
                                        error={validationResults.email}
                                        formSeleniumid={formSeleniumId}
                                        label={l('INVITE_COLLEAGUE.EMAIL_ADDRESS')}
                                        name="email"
                                        onChange={this.updateEmail}
                                        type="email"
                                        value={email}
                                    />
                                    <OrdersRow
                                        formSeleniumid={formSeleniumId}
                                        label={l('INVITE_COLLEAGUE.MESSAGE_LABEL')}
                                        multiline
                                        name="message"
                                        onChange={this.updateMessage}
                                        placeholder={l('INVITE_COLLEAGUE.PLACEHOLDER')}
                                        value={message}
                                    />
                                    <div className="border_top" />

                                    <Button data-seleniumid={`${formSeleniumId}-clear-button`} onClick={this.clear}>
                                        {l('INVITE_COLLEAGUE.CLEAR_BTN')}
                                    </Button>
                                    <PrimaryButton
                                        className="m-l_mini pull-right"
                                        data-seleniumid={`${formSeleniumId}-invite-button`}
                                        disabled={!this.isInviteButtonEnabled()}
                                        onClick={this.invite}
                                    >
                                        {l('INVITE_COLLEAGUE.INVITE_BTN')}
                                    </PrimaryButton>
                                </Layout>
                            </WhiteBlock>
                        )}

                        {!_.isEmpty(invitations.invitations) && (
                            <WhiteBlock className="page p_mini m-btm_md">
                                <h5>{l('INVITE_COLLEAGUE.TITLE_REMAIN_PLURAL')}</h5>
                                {!isInvitationRemains && (
                                    <h6>
                                        {l('INVITE_COLLEAGUE.ABILITY_INVITE_END', {
                                            invitationsTotalNumber: invitations.invitationsTotalNumber,
                                        })}
                                    </h6>
                                )}
                                <div className="border_top" />
                                <div className="table_wrapper">
                                    <table
                                        className="table table-custom InvitedColleagues"
                                        data-seleniumid="table-invited-colleagues"
                                    >
                                        <thead>
                                            <tr className="table-header-bg small">
                                                <th>{l('INVITE_COLLEAGUE.ACCEPTED')}</th>
                                                <th>{l('INVITE_COLLEAGUE.FIRST_NAME')}</th>
                                                <th>{l('INVITE_COLLEAGUE.LAST_NAME')}</th>
                                                <th>{l('INVITE_COLLEAGUE.EMAIL_ADDRESS')}</th>
                                                <th>{l('INVITE_COLLEAGUE.MESSAGE_LABEL')}</th>
                                                <th>{l('INVITE_COLLEAGUE.DATE_SENT')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {invitations.invitations.map(invitation => [
                                                <tr
                                                    key={`${invitation.email}_${invitation.dateSent}`}
                                                    className="small"
                                                >
                                                    <td
                                                        className="cell-custom"
                                                        data-title={l('INVITE_COLLEAGUE.ACCEPTED')}
                                                    >
                                                        <span>{invitation.accepted ? 'Yes' : 'Pending'}</span>
                                                    </td>
                                                    <td
                                                        className="InvitedColleagues-Text cell-custom"
                                                        data-title={l('INVITE_COLLEAGUE.FIRST_NAME')}
                                                    >
                                                        <span>{invitation.firstName}</span>
                                                    </td>
                                                    <td
                                                        className="InvitedColleagues-Text cell-custom"
                                                        data-title={l('INVITE_COLLEAGUE.LAST_NAME')}
                                                    >
                                                        <span>{invitation.lastName}</span>
                                                    </td>
                                                    <td
                                                        className="InvitedColleagues-Text cell-custom"
                                                        data-title={l('INVITE_COLLEAGUE.EMAIL_ADDRESS')}
                                                    >
                                                        <span>{invitation.email}</span>
                                                    </td>
                                                    <td
                                                        className="InvitedColleagues-Text cell-custom"
                                                        data-title={l('INVITE_COLLEAGUE.MESSAGE_LABEL')}
                                                    >
                                                        <span>{invitation.message}</span>
                                                    </td>
                                                    <td
                                                        className="cell-custom"
                                                        data-title={l('INVITE_COLLEAGUE.DATE_SENT')}
                                                    >
                                                        <span>
                                                            {invitation.dateSent &&
                                                                Utils.date(new Date(parseInt(invitation.dateSent, 10)))}
                                                        </span>
                                                    </td>
                                                </tr>,
                                            ])}
                                        </tbody>
                                    </table>
                                </div>
                            </WhiteBlock>
                        )}

                        <Button
                            data-seleniumid={`${formSeleniumId}-dashboard-button`}
                            onClick={routes.navigateToDashboard}
                        >
                            {l('INVITE_COLLEAGUE.BACK_TO_DASHBOARD_BTN')}
                        </Button>
                    </RowMd7>
                </Layout>
            </div>
        );
    }
}

export { InviteColleague };
export default withCodes2(ID.INVITE_COLLEAGUE)(InviteColleague);
